import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';

const textVariants = cva('font-sans tracking-[0.025em] text-foreground', {
  variants: {
    size: {
      inherit: '',
      xs: 'text-tiny',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    },
    color: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      fieldLabel: 'text-field-label',
      emptyValue: 'text-[hsl(var(--empty-value))]',
      accent: 'text-accent-foreground',
      destructive: 'text-destructive',
    },
  },
  defaultVariants: {
    size: 'md',
    color: 'default',
  },
});

type TextElement = 'p' | 'span' | 'div';

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>, VariantProps<typeof textVariants> {
  as?: TextElement;
}

const Text = React.forwardRef<HTMLParagraphElement | HTMLSpanElement | HTMLDivElement, TextProps>(
  ({ as = 'p', className, size, color, ...props }, ref) => {
    const mergedClassName = cn(textVariants({ size, color }), className);

    if (as === 'span') {
      return (
        <span ref={ref as React.Ref<HTMLSpanElement>} className={mergedClassName} {...props} />
      );
    }

    if (as === 'div') {
      return <div ref={ref as React.Ref<HTMLDivElement>} className={mergedClassName} {...props} />;
    }

    return (
      <p ref={ref as React.Ref<HTMLParagraphElement>} className={mergedClassName} {...props} />
    );
  }
);
Text.displayName = 'Text';

export { Text };
